import {
  ContentModule,
  ModuleProperties,
  extractModuleIdentifiers,
  useShowInstructions,
} from '@backstage-components/base';
import {
  RichTextComponent,
  RichTextProps,
} from '@backstage-components/rich-text';
import {Spinner} from '@chakra-ui/react';
import {css, cx} from '@emotion/css';
import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import {SplitText} from 'gsap/SplitText';
import {useSubscription} from 'observable-hooks';
import {FC, useRef} from 'react';
import {
  ComponentDefinition,
  SchemaType,
  reactName,
  textAnimateTriggerEnum,
} from './MagicTextDefinition';
import {OnImagesDidLoad} from './OnImagesDidLoad';
import {HoverItem} from './hoverItem';

import * as Styled from './MagicText.styled';

export interface PositionProperties {
  previous: number;
  current: number;
  amount: number;
}

export interface AnimatableProperties {
  translateX: PositionProperties;
  translateY: PositionProperties;
  rotation: PositionProperties;
}

export interface IMagicTextProps extends ModuleProperties, SchemaType {
  magicTextRichText: RichTextProps;
}

export type MagicTextComponentDefinition = ContentModule<
  typeof reactName,
  IMagicTextProps
>;

export const MagicTextComponent: FC<MagicTextComponentDefinition> = (
  definition
) => {
  const {props} = definition;
  const {magicTextRichText, hoverImages, loaderColor, textAnimateTrigger} =
    props;

  const isAdminScope = definition.config.scope === 'admin';
  const hasHoverImages = hoverImages.length > 0;

  const contentRef = useRef<HTMLDivElement | null>(null);
  const timelineRef = useRef<GSAPTimeline | null>(null);

  // Flag for when the animation is triggered by the flow controller
  const animationTriggered = useRef<boolean>(false);

  const {observable, broadcast} = useShowInstructions(
    ComponentDefinition.instructions,
    definition
  );

  useSubscription(observable, (inst) => {
    if (
      inst.type === `${reactName}:animate-text` &&
      textAnimateTriggerEnum.flowBroadcast
    ) {
      animationTriggered.current = true;
      timelineRef?.current?.play();
    }
  });

  const onImagesLoaded = (): void => {
    if (timelineRef.current !== null) {
      return;
    }
    gsap.registerPlugin(SplitText);
    gsap.registerPlugin(ScrollTrigger);

    // Scope selection to the current module
    const moduleContainer = document.querySelector(
      `.${reactName}-${definition.mid}`
    );

    // Gather all of the em elements in the content
    // Only text with <em> tags will be underlined and have hover image reveals
    const hoverItemElements =
      moduleContainer?.querySelectorAll('.main-content em');

    if (contentRef.current) {
      contentRef.current.style.opacity = '1';
      new SplitText(contentRef.current, {
        type: 'words',
        wordsClass: 'split-parent',
      });

      // Split the lines again so that we can achieve the text reveal
      const splitTextContentChild = new SplitText(contentRef.current, {
        type: 'words',
        wordsClass: 'split-child',
      });

      const lines = splitTextContentChild.words;
      timelineRef.current = gsap.timeline({
        delay: 0.5,
        ...(textAnimateTrigger === textAnimateTriggerEnum.inViewport &&
          !isAdminScope && {
            scrollTrigger: contentRef.current,
          }),
        onStart: () => {
          if (hoverItemElements && hoverItemElements.length) addHoverItems();
        },
        onComplete: () => {
          broadcast({
            type: `${reactName}:on-textAnimationComplete`,
            meta: {},
          });
        },
      });

      timelineRef.current.to(lines, {
        duration: 2.2,
        ease: 'Expo.easeOut',
        startAt: {y: '100%'},
        y: 0,
        delay: (pos: number) => pos * 0.06,
      });

      // When using the flow controller to animate the text, pause the initial animation
      if (
        textAnimateTrigger === textAnimateTriggerEnum.flowBroadcast &&
        !isAdminScope
      ) {
        timelineRef.current.pause();
      }

      // If the animation was aleady triggered but the images were still loading, animate now
      if (animationTriggered.current === true) {
        timelineRef.current.play();
      }

      if (
        !moduleContainer ||
        !hasHoverImages ||
        isAdminScope ||
        !hoverItemElements
      )
        return;

      // Hover item properties that will animate as we move the mouse around the item
      // we will be using interpolation to achieve smooth animations.
      // the “previous” and “current” values are the values to interpolate.
      // the value applied to the element, this case the image element (this.DOM.reveal) will be a value between these two values at a specific increment.
      // the amount is the amount to interpolate.
      const animatableProperties = {
        // translationX
        translateX: {previous: 0, current: 0, amount: 0.08},
        // translationY
        translateY: {previous: 0, current: 0, amount: 0.08},
        // Rotation angle
        rotation: {previous: 0, current: 0, amount: 0.06},
      };

      // Wait to add the hover item images until the magic text animation has started
      const addHoverItems = (): void => {
        // Create a HoverItem for each element
        hoverItemElements.forEach((item, pos) => {
          if (hoverImages[pos]) {
            new HoverItem(
              item,
              pos,
              animatableProperties,
              hoverImages,
              moduleContainer
            );
          }
        });
      };
    }
  };

  const styleClassName = css`
    ${definition.style}
    ${definition.props.styleAttr}
  `;

  return (
    <Styled.Wrapper
      className={cx(styleClassName, `${reactName}-${definition.mid}`)}
      hasHoverImages={hasHoverImages}
    >
      <OnImagesDidLoad
        onLoaded={onImagesLoaded}
        doesImagesUpdated={false}
        classNameWhileLoading={'imagesLoading'}
        classNameOnloaded={'imagesLoaded'}
      >
        <div className="loader">
          <Spinner size="xl" color={loaderColor || 'transparent'} />
        </div>

        <main>
          <div className="main-content" ref={contentRef}>
            <RichTextComponent
              {...extractModuleIdentifiers(definition)}
              mid={`${definition.mid}-magic-rich-text`}
              id={`${definition.id}-magic-rich-text`}
              component="RichText"
              config={definition.config}
              props={magicTextRichText}
            />
          </div>

          <div className="image-preload">
            {hoverImages.map((image) => (
              <img key={image} src={image} />
            ))}
          </div>
        </main>
      </OnImagesDidLoad>
    </Styled.Wrapper>
  );
};
